// src/views/TodayTodoList.jsx
import React, { useState, useEffect } from "react";
import { Container, Breadcrumb, Card } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  Row,
  Col,
  List,
  Checkbox,
  Empty,
  Spin,
  message,
  Progress,
  Tag,
  Button,
  Tooltip,
  Space,
  Avatar,
  Typography,
  Calendar,
  Badge,
} from "antd";
import {
  HomeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  CalendarOutlined,
  FieldTimeOutlined,
  UserOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

const { Text } = Typography;
import { getTodayTodos, updateTodoItemStatus, getMyTodoLists } from "@/api/dailyTodoApi";
import "./TodayTodoList.css";
import dayjs from "dayjs";

const TodayTodoList = () => {
  const [loading, setLoading] = useState(false);
  const [todoList, setTodoList] = useState(null);
  const [updatingItems, setUpdatingItems] = useState({});

  // Calendar state
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [allTodoLists, setAllTodoLists] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);

  useEffect(() => {
    fetchTodayTodos();
    fetchAllTodoLists();
  }, []);

  const fetchTodayTodos = async () => {
    setLoading(true);
    try {
      const data = await getTodayTodos();
      setTodoList(data);
    } catch (error) {
      console.error("Error fetching today's todos:", error);
      message.error(
        error.response?.data?.message || "Bugungi vazifalar yuklanmadi"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTodoLists = async () => {
    setLoadingAll(true);
    try {
      const data = await getMyTodoLists({ page: 1, page_size: 100 });
      setAllTodoLists(data.items || []);
    } catch (error) {
      console.error("Error fetching all todos:", error);
    } finally {
      setLoadingAll(false);
    }
  };

  const handleDateSelect = async (date) => {
    setSelectedDate(date);
    // If selecting today, just refresh
    if (date.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD')) {
      await fetchTodayTodos();
      return;
    }

    // Otherwise, load todo for selected date
    setLoading(true);
    try {
      const params = {
        date_from: date.format("YYYY-MM-DD"),
        date_to: date.format("YYYY-MM-DD"),
      };
      const data = await getMyTodoLists(params);
      if (data.items && data.items.length > 0) {
        setTodoList(data.items[0]);
      } else {
        setTodoList(null);
      }
    } catch (error) {
      console.error("Error fetching todo for date:", error);
      message.error("Vazifalar yuklanmadi");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (itemId, newStatus) => {
    setUpdatingItems((prev) => ({ ...prev, [itemId]: true }));
    try {
      await updateTodoItemStatus(itemId, newStatus);
      message.success("Holat o'zgartirildi");
      await fetchTodayTodos(); // Refresh
    } catch (error) {
      console.error("Error updating status:", error);
      message.error(
        error.response?.data?.message || "Holat o'zgartirishda xatolik"
      );
    } finally {
      setUpdatingItems((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <ClockCircleOutlined style={{ color: "#faad14", fontSize: '22px' }} />,
      in_progress: <SyncOutlined spin style={{ color: "#1890ff", fontSize: '22px' }} />,
      completed: <CheckCircleOutlined style={{ color: "#52c41a", fontSize: '22px' }} />,
      cancelled: <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: '22px' }} />,
    };
    return icons[status] || icons.pending;
  };

  const getStatusText = (status) => {
    const texts = {
      pending: "Kutilmoqda",
      in_progress: "Jarayonda",
      completed: "Bajarildi",
      cancelled: "Bekor qilindi",
    };
    return texts[status] || status;
  };

  const renderItem = (item) => {
    const isCompleted = item.status === "completed";
    const isUpdating = updatingItems[item.id];

    return (
      <List.Item
        className={`todo-item ${isCompleted ? "completed" : ""}`}
        style={{
          padding: '18px 20px',
          marginBottom: '12px',
          background: isCompleted ? '#f6ffed' : '#ffffff',
          border: `2px solid ${isCompleted ? '#52c41a' : '#f0f0f0'}`,
          borderRadius: '12px',
          boxShadow: isCompleted ? '0 2px 8px rgba(82, 196, 26, 0.15)' : '0 2px 4px rgba(0,0,0,0.04)',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = isCompleted
            ? '0 4px 12px rgba(82, 196, 26, 0.25)'
            : '0 4px 12px rgba(0,0,0,0.08)';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = isCompleted
            ? '0 2px 8px rgba(82, 196, 26, 0.15)'
            : '0 2px 4px rgba(0,0,0,0.04)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
        actions={[
          <Tooltip title={getStatusText(item.status)} key="status">
            <div style={{
              fontSize: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {getStatusIcon(item.status)}
            </div>
          </Tooltip>,
          item.status !== "completed" && (
            <Button
              type="primary"
              size="middle"
              onClick={() => handleStatusChange(item.id, "completed")}
              loading={isUpdating}
              key="complete"

              icon={<CheckCircleOutlined />}
            >
              Bajarildi
            </Button>
          ),
          item.status === "completed" && (
            <Button
              size="middle"
              onClick={() => handleStatusChange(item.id, "pending")}
              loading={isUpdating}
              key="reset"

              icon={<CloseCircleOutlined />}
            >
              Qaytarish
            </Button>
          ),
        ].filter(Boolean)}
      >
        <List.Item.Meta
          avatar={
            <Checkbox
              checked={isCompleted}
              onChange={(e) =>
                handleStatusChange(
                  item.id,
                  e.target.checked ? "completed" : "pending"
                )
              }
              disabled={isUpdating}
              style={{
                transform: 'scale(1.3)',
                marginTop: '4px'
              }}
            />
          }
          title={
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {/* Title */}
              <div>
                <span
                  style={{
                    textDecoration: isCompleted ? "line-through" : "none",
                    color: isCompleted ? "#8c8c8c" : "#1a1a1a",
                    fontSize: "16px",
                    fontWeight: 600,
                    lineHeight: '1.5'
                  }}
                >
                  {item.title}
                </span>
              </div>

              {/* Time, Responsible Users, and Note */}
              <Space size="middle" wrap>
                {/* Time */}
                {item.time && (
                  <div style={{
                    padding: '6px 12px',
                    background: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)',
                    borderRadius: '8px',
                    border: '1px solid #91d5ff',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 2px 4px rgba(24, 144, 255, 0.1)'
                  }}>
                    <FieldTimeOutlined style={{ color: '#1890ff', fontSize: '14px' }} />
                    <Text strong style={{ fontSize: '13px', color: '#0050b3' }}>
                      {item.time}
                    </Text>
                  </div>
                )}

                {/* Responsible Users */}
                {item.responsible_users && item.responsible_users.length > 0 && (
                  <div style={{
                    padding: '6px 12px',
                    background: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
                    borderRadius: '8px',
                    border: '1px solid #b7eb8f',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 2px 4px rgba(82, 196, 26, 0.1)'
                  }}>
                    <UserOutlined style={{ color: '#52c41a', fontSize: '14px' }} />
                    <Avatar.Group max={{ count: 3 }} size="small">
                      {item.responsible_users.map(user => (
                        <Tooltip title={user.name} key={user.id}>
                          <Avatar
                            size="small"
                            src={user.picture}
                            icon={<UserOutlined />}
                            style={{
                              border: '2px solid #fff',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          >
                            {!user.picture && user.name?.charAt(0)}
                          </Avatar>
                        </Tooltip>
                      ))}
                    </Avatar.Group>
                    <Text strong style={{ fontSize: '13px', color: '#389e0d' }}>
                      {item.responsible_users.length}
                    </Text>
                  </div>
                )}
              </Space>

              {/* Note */}
              {item.note && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px 14px',
                  background: 'linear-gradient(135deg, #fffbe6 0%, #fff1b8 100%)',
                  borderRadius: '8px',
                  border: '1px solid #ffe58f',
                  borderLeft: '4px solid #faad14',
                  boxShadow: '0 2px 4px rgba(250, 173, 20, 0.1)'
                }}>
                  <Space size="small" align="start">
                    <InfoCircleOutlined style={{ color: '#faad14', fontSize: '14px', marginTop: '3px' }} />
                    <Text style={{ fontSize: '13px', color: '#595959', lineHeight: '1.6' }}>
                      {item.note}
                    </Text>
                  </Space>
                </div>
              )}
            </Space>
          }
        />
      </List.Item>
    );
  };

  if (loading && !todoList && allTodoLists.length === 0) {
    return (
      <Container fluid className="p-4 d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spin size="large" />
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      {/* Breadcrumb */}
      <Breadcrumb className="mb-3">
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/" }}>
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item active>Kunlik vazifalar</Breadcrumb.Item>
      </Breadcrumb>

      {/* Header */}
      {/*<Card className="border-0 shadow-sm mb-4">*/}
      {/*  <Card.Body>*/}
      {/*    <div className="d-flex justify-content-between align-items-center">*/}
      {/*      /!*<div>*!/*/}
      {/*      /!*  <h2 className="mb-1" style={{ color: '#262626', fontWeight: 600, fontSize: '24px' }}>*!/*/}
      {/*      /!*    <CalendarOutlined style={{ marginRight: '8px', color: '#1890ff' }} />*!/*/}
      {/*      /!*    {selectedDate.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD') ? 'Bugungi vazifalar' : 'Tanlangan kun vazifalari'}*!/*/}
      {/*      /!*  </h2>*!/*/}
      {/*      /!*  <p className="mb-0" style={{ color: '#8c8c8c', fontSize: '14px' }}>*!/*/}
      {/*      /!*    {todoList ? new Date(todoList.date).toLocaleDateString("uz-UZ", {*!/*/}
      {/*      /!*      weekday: "long",*!/*/}
      {/*      /!*      year: "numeric",*!/*/}
      {/*      /!*      month: "long",*!/*/}
      {/*      /!*      day: "numeric",*!/*/}
      {/*      /!*    }) : selectedDate.format("DD MMMM YYYY")}*!/*/}
      {/*      /!*  </p>*!/*/}
      {/*      /!*</div>*!/*/}
      {/*      <Space>*/}
      {/*        <Button*/}
      {/*          icon={<ReloadOutlined />}*/}
      {/*          onClick={() => {*/}
      {/*            fetchTodayTodos();*/}
      {/*            fetchAllTodoLists();*/}
      {/*          }}*/}
      {/*          loading={loading || loadingAll}*/}
      {/*        >*/}
      {/*          Yangilash*/}
      {/*        </Button>*/}
      {/*      </Space>*/}
      {/*    </div>*/}
      {/*  </Card.Body>*/}
      {/*</Card>*/}

      {/* 2 Column Layout */}
      <Row gutter={[24, 24]}>
        {/* Left Column - Todo Items List (Large) */}
        <Col xs={24} lg={16}>
          <Card className="border-0 h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="mb-0" style={{ fontSize: '16px', fontWeight: 600 }}>Vazifalar ro'yxati</h5>
                  {todoList && todoList.original_creator_name && (
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                      <UserOutlined style={{ marginRight: '4px' }} />
                      Topshiriq egasi: <Text strong>{todoList.original_creator_name}</Text>
                    </Text>
                  )}
                </div>
                {todoList && todoList.items && (
                  <Tag color="blue" style={{ fontSize: '13px', padding: '4px 12px' }}>
                    {todoList.items.length} ta vazifa
                  </Tag>
                )}
              </div>
              <div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                {todoList && todoList.items && todoList.items.length > 0 ? (
                  <List
                    dataSource={todoList.items.sort((a, b) => a.order - b.order)}
                    renderItem={renderItem}
                    className="todo-items-list"
                  />
                ) : (
                  <Empty
                    description={
                      selectedDate.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD')
                        ? 'Bugun uchun vazifalar yo\'q'
                        : 'Tanlangan kun uchun vazifalar yo\'q'
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  >
                  </Empty>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Right Column - Progress and Calendar (Small) */}
        <Col xs={24} lg={8}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>

            <Card className="border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center mb-3">
                    <CalendarOutlined style={{ color: '#1890ff', fontSize: '16px', marginRight: '8px' }} />
                    <h5 className="mb-0" style={{ fontSize: '15px', fontWeight: 600 }}>Vazifalar taqvimi</h5>
                  </div>
                  <Spin spinning={loadingAll}>
                    <Calendar
                      fullscreen={false}
                      value={selectedDate}
                      onSelect={handleDateSelect}
                      cellRender={(date, info) => {
                        if (info.type !== 'date') return info.originNode;

                        const todosForDate = allTodoLists.filter(
                          todo => dayjs(todo.date).format("YYYY-MM-DD") === date.format("YYYY-MM-DD")
                        );
                        if (todosForDate.length > 0) {
                          const todo = todosForDate[0];
                          const isToday = date.format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD");
                          const isPast = date.isBefore(dayjs(), 'day');

                          return (
                            <div style={{
                              fontSize: '10px',
                              textAlign: 'center',
                            }}>
                              <Badge
                                count={todo.total_items}
                                style={{
                                  backgroundColor: isToday ? '#1890ff' : isPast ? '#d9d9d9' : '#52c41a',
                                  fontSize: '9px',
                                  height: '16px',
                                  minWidth: '16px',
                                  lineHeight: '16px'
                                }}
                              />
                              <div style={{
                                color: isToday ? '#1890ff' : isPast ? '#8c8c8c' : '#52c41a',
                                marginTop: '2px',
                                fontWeight: 500,
                                fontSize: '10px'
                              }}>
                                {todo.completed_items}/{todo.total_items}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </Spin>

                  {/* Legend */}
                  <div style={{
                    marginTop: '16px',
                    padding: '12px',
                    background: '#fafafa',
                    borderRadius: '6px',
                    border: '1px solid #f0f0f0'
                  }}>
                    <Space size="middle">
                      <Space size="small">
                        <div style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: '#1890ff'
                        }} />
                        <Text style={{ fontSize: '12px' }}>Bugun</Text>
                      </Space>
                      <Space size="small">
                        <div style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: '#52c41a'
                        }} />
                        <Text style={{ fontSize: '12px' }}>Kelajak</Text>
                      </Space>
                      <Space size="small">
                        <div style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: '#d9d9d9'
                        }} />
                        <Text style={{ fontSize: '12px' }}>O'tgan</Text>
                      </Space>
                    </Space>
                  </div>
                </Card.Body>
              </Card>
            {/* Progress Card */}
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center mb-3">
                  <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px', marginRight: '8px' }} />
                  <h5 className="mb-0" style={{ fontSize: '15px', fontWeight: 600 }}>Bajarilish</h5>
                </div>
                <div style={{ textAlign: 'center' }}>
                  {todoList ? (
                    <>
                      <Progress
                        type="circle"
                        percent={todoList.completion_percentage}
                        strokeColor="#1890ff"
                        trailColor="#f0f0f0"
                        strokeWidth={6}
                        size={120}
                      />
                      <div style={{ marginTop: '16px' }}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '10px 12px',
                            background: '#fafafa',
                            borderRadius: '6px'
                          }}>
                            <Text style={{ fontSize: '14px' }}>Jami:</Text>
                            <Text strong style={{ fontSize: '14px' }}>{todoList.total_items}</Text>
                          </div>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '10px 12px',
                            background: '#f6ffed',
                            borderRadius: '6px'
                          }}>
                            <Text style={{ fontSize: '14px' }}>Bajarildi:</Text>
                            <Text strong style={{ fontSize: '14px', color: '#52c41a' }}>{todoList.completed_items}</Text>
                          </div>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '10px 12px',
                            background: '#fffbe6',
                            borderRadius: '6px'
                          }}>
                            <Text style={{ fontSize: '14px' }}>Qoldi:</Text>
                            <Text strong style={{ fontSize: '14px', color: '#faad14' }}>{todoList.pending_items}</Text>
                          </div>
                        </Space>
                      </div>
                    </>
                  ) : (
                    <Empty
                      description="Ma'lumot yo'q"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  )}
                </div>
              </Card.Body>
            </Card>

            {/* Overall Statistics */}
            {allTodoLists.length > 0 && (
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center mb-3">
                    <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '16px', marginRight: '8px' }} />
                    <h5 className="mb-0" style={{ fontSize: '15px', fontWeight: 600 }}>Umumiy statistika</h5>
                  </div>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      background: '#fafafa',
                      borderRadius: '6px'
                    }}>
                      <Text style={{ fontSize: '14px' }}>Jami vazifalar:</Text>
                      <Text strong style={{ fontSize: '14px' }}>
                        {allTodoLists.reduce((sum, t) => sum + t.total_items, 0)}
                      </Text>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      background: '#f6ffed',
                      borderRadius: '6px'
                    }}>
                      <Text style={{ fontSize: '14px' }}>Bajarilgan:</Text>
                      <Text strong style={{ fontSize: '14px', color: '#52c41a' }}>
                        {allTodoLists.reduce((sum, t) => sum + t.completed_items, 0)}
                      </Text>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      background: '#fffbe6',
                      borderRadius: '6px'
                    }}>
                      <Text style={{ fontSize: '14px' }}>Qolgan:</Text>
                      <Text strong style={{ fontSize: '14px', color: '#faad14' }}>
                        {allTodoLists.reduce((sum, t) => sum + t.pending_items, 0)}
                      </Text>
                    </div>
                  </Space>
                </Card.Body>
              </Card>
            )}


          </Space>
        </Col>
      </Row>
    </Container>
  );
};

export default TodayTodoList;
